import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PackagingGiftSetView.css';

/**
 * Gift Set Assembly View for Packaging Department
 * Groups component carts by parent work order for assembly
 */
function PackagingGiftSetView({ onAssemblyComplete }) {
  const [groupedCarts, setGroupedCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assembling, setAssembling] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  useEffect(() => {
    fetchGroupedCarts();
    // Refresh every 30 seconds
    const interval = setInterval(fetchGroupedCarts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchGroupedCarts = async () => {
    try {
      const response = await axios.get('/api/departments/packaging/carts-grouped');
      setGroupedCarts(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching grouped carts:', err);
      setError('Failed to load packaging carts');
    } finally {
      setLoading(false);
    }
  };

  const toggleGroupExpansion = (parentWONumber) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(parentWONumber)) {
      newExpanded.delete(parentWONumber);
    } else {
      newExpanded.add(parentWONumber);
    }
    setExpandedGroups(newExpanded);
  };

  const checkComponentStatus = (cart) => {
    // Check if cart has been scanned in
    const scanTracking = cart.scan_tracking || [];
    const scannedIn = scanTracking.some(s =>
      s.department === 'packaging' &&
      s.scan_type === 'in' &&
      !s.scan_out_time
    );
    return scannedIn ? 'scanned' : 'pending';
  };

  const checkGroupReadiness = (group) => {
    return group.componentCarts.every(cart =>
      checkComponentStatus(cart) === 'scanned'
    );
  };

  const handleCompleteAssembly = async (group) => {
    if (!window.confirm(`Complete assembly for gift set ${group.parentWorkOrderNumber}?`)) {
      return;
    }

    setAssembling(group.parentWorkOrderNumber);

    try {
      const componentCartIds = group.componentCarts.map(c => c.id);

      const response = await axios.post('/api/carts/create-parent-cart', {
        parentWorkOrderId: group.parentWorkOrderId,
        componentCartIds
      });

      alert(`✓ Gift set cart ${response.data.barcode} created successfully!`);

      // Refresh the list
      await fetchGroupedCarts();

      if (onAssemblyComplete) {
        onAssemblyComplete(response.data);
      }
    } catch (error) {
      console.error('Error completing assembly:', error);
      alert(`Failed to complete assembly: ${error.response?.data?.error || error.message}`);
    } finally {
      setAssembling(null);
    }
  };

  if (loading) {
    return <div className="packaging-gift-set-view loading">Loading...</div>;
  }

  if (error) {
    return <div className="packaging-gift-set-view error">{error}</div>;
  }

  // Separate gift sets from regular work orders
  const giftSetGroups = groupedCarts.filter(g => g.isGiftSet);
  const regularGroups = groupedCarts.filter(g => !g.isGiftSet);

  return (
    <div className="packaging-gift-set-view">
      {giftSetGroups.length > 0 && (
        <div className="gift-set-section">
          <h3 className="section-title">
            🎁 Gift Sets Ready for Assembly ({giftSetGroups.length})
          </h3>

          {giftSetGroups.map(group => {
            const isExpanded = expandedGroups.has(group.parentWorkOrderNumber);
            const isReady = checkGroupReadiness(group);
            const scannedCount = group.componentCarts.filter(c =>
              checkComponentStatus(c) === 'scanned'
            ).length;

            return (
              <div
                key={group.parentWorkOrderNumber}
                className={`gift-set-group ${isReady ? 'ready' : 'pending'}`}
              >
                <div
                  className="group-header"
                  onClick={() => toggleGroupExpansion(group.parentWorkOrderNumber)}
                >
                  <div className="header-left">
                    <span className="expand-icon">
                      {isExpanded ? '▼' : '▶'}
                    </span>
                    <span className="wo-number">WO-{group.parentWorkOrderNumber}</span>
                    <span className="component-count">
                      {group.componentCarts.length} components
                    </span>
                  </div>

                  <div className="header-right">
                    <span className={`status-badge ${isReady ? 'ready' : 'pending'}`}>
                      {scannedCount}/{group.componentCarts.length} scanned
                    </span>
                    {isReady && (
                      <span className="ready-indicator">✓ Ready</span>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="component-carts">
                    <table className="components-table">
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Cart</th>
                          <th>Component</th>
                          <th>Fragrance</th>
                          <th>Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.componentCarts.map(cart => {
                          const status = checkComponentStatus(cart);
                          const firstItem = cart.cart_items?.[0];

                          return (
                            <tr key={cart.id} className={`component-row ${status}`}>
                              <td>
                                <span className={`status-icon ${status}`}>
                                  {status === 'scanned' ? '✓' : '○'}
                                </span>
                              </td>
                              <td className="cart-number">{cart.cart_number}</td>
                              <td className="component-sku">
                                {cart.workOrder?.item_sku || firstItem?.item_sku}
                              </td>
                              <td className="fragrance">
                                {firstItem?.fragrance_name}
                              </td>
                              <td className="quantity">
                                {cart.cart_items?.reduce((sum, item) =>
                                  sum + (item.quantity || 0), 0
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {isReady && (
                      <div className="assembly-action">
                        <button
                          className="complete-assembly-btn"
                          onClick={() => handleCompleteAssembly(group)}
                          disabled={assembling === group.parentWorkOrderNumber}
                        >
                          {assembling === group.parentWorkOrderNumber
                            ? 'Creating...'
                            : '✓ Complete Gift Set Assembly'}
                        </button>
                        <p className="assembly-note">
                          This will create a cart for the assembled gift sets and move it to carting
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {regularGroups.length > 0 && (
        <div className="regular-carts-section">
          <h3 className="section-title">
            Regular Work Orders ({regularGroups.length})
          </h3>
          <div className="regular-carts-list">
            {regularGroups.map(group => (
              <div key={group.parentWorkOrderNumber} className="regular-cart-item">
                <span className="wo-number">WO-{group.parentWorkOrderNumber}</span>
                <span className="cart-count">
                  {group.componentCarts.length} cart{group.componentCarts.length !== 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {groupedCarts.length === 0 && (
        <div className="empty-state">
          <p>No carts in packaging department</p>
        </div>
      )}
    </div>
  );
}

export default PackagingGiftSetView;
